import { HubScriptLogsWsUrl, ScriptingKeyword } from '../consts'
import { useEffect } from 'react'
import { useRecoilState, useRecoilValue } from 'recoil'
import scriptLogsAtom from '../recoil/scriptLogs'
import trafficPlaybackAtom, { TRAFFIC_PLAYBACK_OK } from '../recoil/trafficPlayback/atom'
import { appStopTrafficCapturingDisabled } from '../types/global'
import packetCapturingStoppedAtom from '../recoil/packetCapturingStopped'
import { useAuth } from '../components/UI/Auth/Auth'
import useScriptLogWebSocket, { THROTTLE_WS_STATE_CHANGE_MS } from './useScriptLogWebSocket'
import { useInterval } from '../helpers/interval'
import scriptAllWsLogsAtom from '../recoil/scriptAllWsLogs'
import scriptLogsUnreadAtom from '../recoil/scriptLogsUnread'

const MAX_LOGS = 10000
const DELETE_TOP_LOGS_NUM = 5000

export interface ScriptLogs {
  scriptIndex: number
  unread?: boolean
  logAutoRemovalCount: number
  logs: string[]
}

const consoleClearedLog = '🧹 console was cleared \n\n'
const previousLogsRemovedLog = 'previous logs were ♻️ auto-removed to maintain app memory \n\n'

const useScriptLogTracker = () => {
  const { shouldAuthenticate } = useAuth()

  const trafficPlayback = useRecoilValue(trafficPlaybackAtom)
  const capturingStopped = useRecoilValue(packetCapturingStoppedAtom)
  const [scriptLogs, setScriptLogs] = useRecoilState(scriptLogsAtom)
  const [scriptLogsUnread, setScriptLogsUnread] = useRecoilState(scriptLogsUnreadAtom)

  const [receivedScriptLogs, setReceivedScriptLogs] = useRecoilState(scriptAllWsLogsAtom)

  useEffect(() => {
    if (scriptLogsUnread) {
      return
    }

    const unreadLogsFound = Object.values(scriptLogs).findIndex((scriptLog: ScriptLogs) => {
      return scriptLog?.unread
    })

    setScriptLogsUnread(unreadLogsFound !== -1)
  }, [scriptLogs])

  useInterval(() => {
    setScriptLogs(receivedScriptLogs)
  }, 1000, true)

  const { open, close, reopen } = useScriptLogWebSocket({
    wsUrl: HubScriptLogsWsUrl,
    withAuth: false,
    withIdleTimer: false,
    onMessage: (e) => {
      if (!e?.data) {
        return
      }

      const logLine = e.data
      const logChunks = logLine.split(' ')
      const logPrefixStr = logChunks[0].replace(/[[\]]/g, '')
      const logPrefix = logPrefixStr.split('-')
      const logMessage = logChunks[1]
      const scriptIndex: number = parseInt(logPrefix[logPrefix.length - 1])

      const logHost = logPrefixStr.replace(/-\d$/g, '')

      setReceivedScriptLogs(prevLogs => {
        const updatedLogs = {...prevLogs}
        const scriptLog: ScriptLogs = {...updatedLogs[scriptIndex]}

        if (Object.keys(scriptLog).length === 0) {
          updatedLogs[scriptIndex] = {
            scriptIndex,
            unread: true,
            logAutoRemovalCount: 0,
            logs: [`${logLine}\n`]
          } as ScriptLogs
        } else {
          const updatedScriptLogs = [...scriptLog.logs]

          if (updatedScriptLogs.length > MAX_LOGS) {
            updatedScriptLogs.splice(0, DELETE_TOP_LOGS_NUM)
            scriptLog.logAutoRemovalCount += 1

            const logsRemovedCount = scriptLog.logAutoRemovalCount * DELETE_TOP_LOGS_NUM
            if (logsRemovedCount > 0) {
              updatedScriptLogs.unshift(`Note: ${logsRemovedCount} ${previousLogsRemovedLog}`)
            }
          }

          updatedScriptLogs.push(`${logLine}\n`)
          scriptLog.logs = updatedScriptLogs
          scriptLog.unread = true
          updatedLogs[scriptIndex] = scriptLog
        }

        if (logMessage === ScriptingKeyword.ClearConsoleLogs) {
          // Smart logs cleaning per host
          updatedLogs[scriptIndex].logs = updatedLogs[scriptIndex].logs.filter(logLine => {
            if (logLine.includes(ScriptingKeyword.ClearConsoleLogs)) {
              return false
            }

            return !logLine.includes(logHost)
          })

          if (updatedLogs[scriptIndex].logs[0] === consoleClearedLog) {
            updatedLogs[scriptIndex].logs.splice(0, 1)
          }

          updatedLogs[scriptIndex].logs.unshift(consoleClearedLog)
        }

        return updatedLogs
      })
    },
    onClose: () => {
      console.log('Trying to reopen script logs websocket...')
      setTimeout(() => reopen(), THROTTLE_WS_STATE_CHANGE_MS + 100)
    },
    onError: (e) => {
      console.error('Script logs websocket error', e)
      console.log('Trying to reopen script logs websocket...')
      setTimeout(() => reopen(), THROTTLE_WS_STATE_CHANGE_MS + 100)
    }
  })

  useEffect(() => {
    if (trafficPlayback !== TRAFFIC_PLAYBACK_OK || shouldAuthenticate) {
      close()
      return
    }

    if (
      !appStopTrafficCapturingDisabled() &&
      (capturingStopped === null || capturingStopped)
    ) {
      close()
      return
    }

    open()

    return () => close()
  }, [trafficPlayback, shouldAuthenticate, capturingStopped])
}

export default useScriptLogTracker
